import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Hangar } from '../../../../types';
import { InboxItem, getUrgencyLevel, URGENCY_COLORS, URGENCY_LABELS } from '../../../OperationsInbox/types';

type Props = {
  item: InboxItem;
  availableHangars: Hangar[];
  onAssign: (tripId: string, hangarId: string) => Promise<void>;
  isProcessing: boolean;
};

/**
 * Format date for compact display
 */
function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Simplified trip item card for the Ops queue
 * Shows trips that need hangar assignment
 */
export const TripItem: React.FC<Props> = ({ item, availableHangars, onAssign, isProcessing }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isCompleting, setIsCompleting] = React.useState(false);
  const [selectedHangar, setSelectedHangar] = React.useState<string>('');

  // Get urgency level for visual indicator
  const urgency = getUrgencyLevel(item.arrival_date);
  const urgencyColor = URGENCY_COLORS[urgency];
  const urgencyLabel = URGENCY_LABELS[urgency];

  // Add fit check to hangars
  const hangarsWithFit = React.useMemo(() => {
    return availableHangars.map((h) => ({
      ...h,
      fits: !item.wingspan || item.wingspan <= h.width,
    }));
  }, [availableHangars, item.wingspan]);

  const handleHangarSelect = async (hangarId: string) => {
    if (!hangarId || !item.trip_id) return;

    try {
      setIsCompleting(true);
      setSelectedHangar(hangarId);
      await onAssign(item.trip_id, hangarId);
    } catch (err) {
      setIsCompleting(false);
      setSelectedHangar('');
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderLeft: '3px solid',
        borderLeftColor: urgencyColor || theme.palette.warning.main,
        bgcolor: isCompleting ? theme.palette.success.light + '10' : theme.palette.background.paper,
        opacity: isCompleting ? 0.7 : 1,
        transition: 'all 150ms ease-out',
      }}
    >
      {/* Row 1: Tail, Type */}
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        {/* Urgency Badge */}
        {urgencyLabel && (
          <Chip
            label={urgencyLabel}
            size="small"
            sx={{
              bgcolor: urgencyColor,
              color: 'white',
              fontWeight: 700,
              fontSize: 9,
              height: 18,
              minWidth: 50,
            }}
          />
        )}

        {/* Tail Number */}
        <Chip
          label={item.tail_number}
          size="small"
          sx={{ fontWeight: 700, fontSize: 12 }}
        />

        {/* Aircraft Type */}
        <Typography
          variant="body2"
          sx={{
            fontSize: 12,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.aircraft_make} {item.aircraft_model}
        </Typography>

        {/* Completion indicator */}
        {isCompleting && (
          <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />
        )}
      </Box>

      {/* Row 2: Dates + Assign */}
      <Box display="flex" alignItems="center" gap={1}>
        {/* Arrival */}
        <Box display="flex" alignItems="center" gap={0.5}>
          <FlightLandIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {formatDate(item.arrival_date)}
          </Typography>
        </Box>

        <Typography variant="caption" color="text.secondary">
          →
        </Typography>

        {/* Departure */}
        <Typography variant="caption" color="text.secondary">
          {formatDate(item.departure_date)}
        </Typography>

        {/* Assign Dropdown */}
        {!isCompleting && (
          <FormControl size="small" sx={{ ml: 'auto', minWidth: 100 }}>
            <Select
              value={selectedHangar}
              displayEmpty
              onChange={(e) => handleHangarSelect(e.target.value)}
              disabled={isProcessing || hangarsWithFit.length === 0}
              sx={{
                fontSize: 11,
                height: 28,
                '& .MuiSelect-select': {
                  py: 0.5,
                },
              }}
            >
              <MenuItem value="" disabled>
                <em>{t('screens.ops.queue.actions.assign')}...</em>
              </MenuItem>
              {hangarsWithFit.map((hangar) => (
                <MenuItem
                  key={hangar.id}
                  value={hangar.id}
                  disabled={!hangar.fits}
                  sx={{ fontSize: 12 }}
                >
                  {hangar.name}
                  {!hangar.fits && ' (too small)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    </Paper>
  );
};
